import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldOff, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../types';

/**
 * Shown instead of a restricted page.
 *
 * Deliberately not a silent redirect to the dashboard: someone opening an old
 * bookmark would land somewhere else with no idea why, and would file a ticket
 * about a "broken link". Naming the required role turns that into a request
 * their admin can act on directly.
 */
export default function NoAccess({ required }: { required: UserRole[] }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="mx-auto mt-12 max-w-lg rounded-xl border border-slate-200 bg-surface p-8 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
        <ShieldOff size={22} className="text-slate-500" />
      </div>
      <h2 className="text-lg font-semibold text-slate-900">{t('noAccess.title')}</h2>
      <p className="mt-2 text-sm text-slate-600">
        {t('noAccess.restrictedTo', {
          roles: required.map((r) => t(`roles.${r}`)).join(', '),
        })}
      </p>
      {user?.role && (
        <p className="mt-1 text-sm text-slate-500">
          {t('noAccess.yourRole', { role: t(`roles.${user.role}`) })}
        </p>
      )}
      <p className="mt-4 text-xs text-slate-500">{t('noAccess.askAdmin')}</p>
      <button
        onClick={() => navigate(-1)}
        className="mt-6 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
      >
        <ArrowLeft size={14} /> {t('common.back')}
      </button>
    </div>
  );
}
